import { useState, useCallback } from 'react';

import toolAccessApi from '../data/toolAccessApi';
import type { GetActiveToolAccessRequestForScopeResponse } from '../../__generated__/chirp/com/hubspot/tool/access/rpc/ToolAccessService';
import { User } from '../types/User';
import { ACTIVE } from '../constants/toolAccessRequestStatus';
import { trackRequestCreated } from '../utils/usageTracker';
import { reportRequestException } from '../utils/sentryUtils';
import { Approver } from '../types/Approver';
import { ToolAccessErrorResponse } from '../types/ToolAccessErrorResponse';
import { ToolAccessData } from '../types/ToolAccessData';

type UseToolAccessArgs = {
  scope: string;
  toolName?: string;
};

type UseToolAccessState = {
  error: any;
  loading: boolean;
  modalOpen: boolean;
  request:
    | null
    | GetActiveToolAccessRequestForScopeResponse['activeToolAccessRequest'];
  requestCreated: boolean; // flag for when user has submitted a request in this session
  scope: string;
  toolName?: string;
};

type CreateRequestData = {
  message: string;
  approver: User;
};

export type ToolAccessEntity = ReturnType<typeof useToolAccess>;

export const useToolAccess = (args: UseToolAccessArgs) => {
  const { scope, toolName } = args;
  const [state, setState] = useState<UseToolAccessState>({
    error: null, // Fetch error
    loading: false,
    modalOpen: false,
    request: null,
    requestCreated: false, // flag for when user has submitted a request in this session
    scope,
    toolName,
  });

  const openModal = () => {
    setState((prevState) => ({
      ...prevState,
      modalOpen: true,
    }));
  };

  const closeModal = () => {
    setState((prevState) => ({
      ...prevState,
      modalOpen: false,
    }));
  };

  const fetchRequest = useCallback(() => {
    if (state.loading === true || state.request !== null) {
      return;
    }
    setState((prevState) => ({ ...prevState, loading: true, error: null }));
    toolAccessApi
      .fetchRequestByScope(scope)
      .then((response) => {
        setState((prevState) => ({
          ...prevState,
          toolName: toolName || response.toolName,
          loading: false,
          request: response.activeToolAccessRequest,
          status: response.status,
        }));
      })
      .catch((error: ToolAccessErrorResponse) => {
        setState((prevState) => ({
          ...prevState,
          error: error.cause?.cause ?? true,
          loading: false,
        }));
        reportRequestException('fetchRequest', error, { scope });
      });
  }, [scope, state.loading, state.request, toolName]);

  const createRequest = useCallback(
    (
      data: CreateRequestData,
      onComplete: (data: ToolAccessData) => void,
      onError: (err: ToolAccessErrorResponse) => void
    ) => {
      setState((prevState) => ({
        ...prevState,
        loading: true,
      }));
      const { message, approver } = data;
      toolAccessApi
        .createRequest({
          scope,
          approverUserId: approver.id,
          message,
        })
        .then((response) => {
          const { createdAt, id, expiresAt, portalId, roleName } = response;
          const inferredApprover: Approver = {
            ...approver,
            userId: approver.id,
          };
          const request = {
            approver: inferredApprover,
            createdAt,
            id,
            expiresAt,
            portalId,
          };
          setState((prevState) => ({
            ...prevState,
            loading: false,
            request,
            requestCreated: true,
            status: ACTIVE,
          }));
          trackRequestCreated({ roleName, scope });
          if (typeof onComplete === 'function') {
            onComplete({ message, approver: inferredApprover });
          }
        })
        .catch((error) => {
          if (typeof onError === 'function') {
            onError(error);
          }
          reportRequestException('createRequest', error, { scope });
        })
        .finally(() =>
          setState((prevState) => ({ ...prevState, loading: false }))
        );
    },
    [scope]
  );

  return {
    openModal,
    closeModal,
    createRequest,
    fetchRequest,
    ...state,
  };
};
